import React, { useMemo, useState } from 'react'
import moment from 'moment'
import DeliverablesPreview from '../components/DeliverablesPreview'
import { Modal, ModalBody } from 'reactstrap'
import { useSelector } from 'react-redux'
import { RootState, store } from 'store'
import { useParams } from 'react-router-dom'
import AccessControl from 'wrappers/AccessControl'
import { PRIVILEGE } from 'constants/privilege'
import FormFields from '../form/FormFields'
import { processCollaborationMilestoneStep } from 'services/api/collaboration'
import { MilestoneStep, MilestoneStepFeedback, GigStepPropertyWithValue } from 'types/collaboration'
import { toast } from 'react-toastify'
import HTMLContent from 'Components/Common/HTMLContent'

interface CurrentStepProps {
    currentStep: MilestoneStep,
    setOpenedStep: (slug: string) => void,
    feedbacks: MilestoneStepFeedback[]
    onFeedbackView: (feedback: string) => void,
    addDeadline: () => void,
    onProcess?: () => void,
    setSelectedMedia: (media: any) => void
}


const CurrentStep: React.FC<CurrentStepProps> = ({ currentStep, setOpenedStep, feedbacks, onFeedbackView, addDeadline, onProcess, setSelectedMedia }) => {

    const hubId = useSelector((state: RootState) => state.auth.selectedHubId);

    const [approvalModalVisibility, showApprovalModal] = useState(false);
    const [rejectionModalVisibility, showRejectionModal] = useState(false);

    const [reaction, setReaction] = useState('');
    const [feedbackText, setFeedbackText] = useState("");

    const { campaignId, influencerId, milestoneId } = useParams();
    const milestoneData = useSelector((state: RootState) => state.collaborations.milestones[campaignId!]?.[influencerId!]?.[milestoneId!]?.milestone);
    const activeMilestone = milestoneData?.milestone ?? null;

    const [submitting, setSubmitting] = useState(false);
    const [properties, setProperties] = useState<GigStepPropertyWithValue[]>([]);

    const influencer = useSelector((state: RootState) => state.collaborations?.items[campaignId!]?.[influencerId!]?.influencer);

    const feedback = useMemo(() => {
        const stepFeedbacks = (Array.isArray(feedbacks) ? feedbacks : []).filter(
            (feedback) => feedback.step === currentStep.gig_step.slug
        );

        if (stepFeedbacks.length === 0) return null;

        const sorted = stepFeedbacks.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        return sorted[0]; // most recent one
    }, [feedbacks, currentStep]);

    const stepTitle = useMemo(() => {
        const title = (currentStep.gig_step.title ?? (currentStep.gig_step.name ?? "") + "*");
        if (feedback && feedback.revision_required) {
            return currentStep.gig_step.revision_required_title ?? title + "*";
        }

        if(!currentStep.can_process && currentStep.gig_step?.non_assignee_title){
            return currentStep.gig_step.non_assignee_title;
        }


        return title
    }, [currentStep, feedback])

    const approval = useMemo(() => {
        const approvalProperty = currentStep.gig_step.properties.find((property) => property.type === 'approval');
        return !!approvalProperty;
    }, [currentStep])

    const processStep = async (approval: 'yes' | 'no') => {

        if (!hubId || !campaignId || !influencerId || !milestoneId) return;

        try {
            setSubmitting(true)
            const result = await processCollaborationMilestoneStep(hubId, campaignId, influencerId, milestoneId, {
                step: currentStep.gig_step.slug,
                approval,
                ...(feedbackText ? { feedback: feedbackText } : {}),
                ...(reaction ? { reaction } : {}),
                ...((approval === 'yes' && properties.length > 0) ? Object.fromEntries(properties.filter((prop) => !['approval'].includes(prop.key)).map(({ key, value }) => [key, value])) : {})
            });

            console.log(result);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
            showApprovalModal(false)
            showRejectionModal(false);
            if (onProcess) {
                onProcess();
            }
        }
    }

    const validPropertyValues = useMemo(() => {
        for (let property of properties) {
            if(['approval'].includes(property.key)) continue;
            if (property.mandatory && !property.value) return false;
        }

        return true;
    }, [properties])


    return (
        <>
            <div className="position-relative">

                <div className={`step-card ${currentStep.complete ? '' : (currentStep.can_process ? '' : 'waiting')}`}>
                    <div className='step-card-1 d-flex justify-content-between align-items-center'>
                        {
                            currentStep.complete ? (
                                <div className="next-step-badge">
                                    Complete
                                </div>
                            ) : (
                                currentStep.can_process ?
                                    (
                                        <div className="next-step-badge">
                                            Next Step
                                        </div>
                                    ) :
                                    (
                                        <div className="waiting-badge">
                                            Waiting
                                        </div>
                                    )
                            )
                        }
                        {
                            currentStep.gig_step?.help_url && <a href={currentStep.gig_step.help_url} target="_blank" rel="noopener noreferrer" className='d-flex align-items-center gap-1'>
                                <span> <i className='ri-question-line'></i> </span>
                                <span>Help</span>
                            </a>
                        }
                    </div>
                    <div className="step-card-2">
                        <div className='d-flex flex-column'>
                            <span className='pop gig-step-title'>{stepTitle}</span>
                            <span className="gig-step-description">
                                <HTMLContent content={currentStep.gig_step.description ?? ''} />
                            </span>
                        </div>
                        <DeliverablesPreview setSelectedMedia={setSelectedMedia} step={currentStep} />
                        {
                            currentStep.can_process &&
                            <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.PROCESS}>
                                {
                                    approval ? (
                                        <div className="d-flex justify-content-between align-items-center gap-3">
                                            <button className="theme-btn theme-btn-primary-outlined w-100" onClick={() => showRejectionModal(true)}>Decline</button>
                                            <button className="theme-btn theme-btn-primary w-100" onClick={() => showApprovalModal(true)}>Approve</button>
                                        </div>
                                    ) :
                                        (
                                            !['reel_approval', 'post_approval', 'story_approval', 'ig_media_approval', 'video_approval'].includes(currentStep.gig_step.slug) ?
                                                <button onClick={() => setOpenedStep(currentStep.gig_step.slug)} className="theme-btn theme-btn-primary">{currentStep.gig_step.cta_text ?? 'Take Action*'}</button> : null
                                        )
                                }
                            </AccessControl>

                        }
                    </div>

                </div>
                {
                    currentStep.gig_step.require_deadline && !currentStep.deadline &&
                    <div className={currentStep.can_process ? 'add-step-deadline' : 'step-deadline'}>
                        <span>Add expected completion date</span>
                        <span className='add-deadline-action' onClick={() => addDeadline()}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_635_3228)">
                                    <path d="M8.49967 6.00065C8.49967 5.72451 8.27582 5.50065 7.99967 5.50065C7.72353 5.50065 7.49967 5.72451 7.49967 6.00065L7.49967 7.50067H5.99967C5.72353 7.50067 5.49967 7.72453 5.49967 8.00067C5.49967 8.27681 5.72353 8.50067 5.99967 8.50067H7.49967V10.0007C7.49967 10.2768 7.72353 10.5007 7.99967 10.5007C8.27582 10.5007 8.49967 10.2768 8.49967 10.0007L8.49967 8.50067H9.99967C10.2758 8.50067 10.4997 8.27681 10.4997 8.00067C10.4997 7.72452 10.2758 7.50067 9.99967 7.50067H8.49967V6.00065Z" fill="#007AFF" />
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.99967 0.833984C4.04163 0.833984 0.833008 4.04261 0.833008 8.00065C0.833008 11.9587 4.04163 15.1673 7.99967 15.1673C11.9577 15.1673 15.1663 11.9587 15.1663 8.00065C15.1663 4.04261 11.9577 0.833984 7.99967 0.833984ZM1.83301 8.00065C1.83301 4.5949 4.59392 1.83398 7.99967 1.83398C11.4054 1.83398 14.1663 4.5949 14.1663 8.00065C14.1663 11.4064 11.4054 14.1673 7.99967 14.1673C4.59392 14.1673 1.83301 11.4064 1.83301 8.00065Z" fill="#007AFF" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_635_3228">
                                        <rect width="16" height="16" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>

                            ADD DATE
                        </span>
                    </div>
                }
                {
                    currentStep.can_process && currentStep.deadline &&
                    <div className='add-step-deadline'>
                        <span>Expected completion by {moment.utc(currentStep.deadline).local().format('MMM DD, hh:mm A')} </span>
                    </div>
                }
                {
                    !currentStep.can_process && currentStep.deadline &&
                    <div className='step-deadline'>
                        <span>Expected completion by {moment.utc(currentStep.deadline).local().format('MMM DD, hh:mm A')} </span>
                    </div>
                }
            </div>
            <Modal className='approve-reject-modal' centered isOpen={approvalModalVisibility} toggle={() => {
                setFeedbackText('');
                showApprovalModal(false)
            }}>
                <ModalBody className='p-0'>
                    <div className='approve-reject-modal-content'>
                        <div className="reaction">
                            {/* <h4>Glad you liked {influencer?.name?.split(' ')?.[0] ?? 'influencer'}'s idea!</h4>
                            <span>Pick a reaction to let the influencer know how you feel. A little love goes a long way! 💬✨</span>
                            <div className="d-flex justify-content-between align-items-center gap-2 mt-2">
                                <div className={`reaction-btn ${reaction === 'good' ? 'active' : ''}`} onClick={() => setReaction('good')}>👍 Good</div>
                                <div className={`reaction-btn ${reaction === 'better' ? 'active' : ''}`} onClick={() => setReaction('better')}>❤️️ Better</div>
                                <div className={`reaction-btn ${reaction === 'excellent' ? 'active' : ''}`} onClick={() => setReaction('excellent')}>🔥️️ Excellent</div>
                            </div> */}
                        </div>
                        <div className="feedback">
                            <h4>Leave feedback for next steps (optional)</h4>
                            <span>A little feedback goes a long way in inspiring great content! ✨</span>
                            <textarea placeholder='Share any suggestions, edits, or next steps...' value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} className='feedback-textarea mt-2' rows={5} />
                            <div className="mt-3">
                                <FormFields activeMilestone={activeMilestone} step={currentStep} onChange={(updatedProperties) => setProperties(updatedProperties)} />
                            </div>
                        </div>
                        <button className="theme-btn theme-btn-primary w-100" disabled={submitting || !validPropertyValues} onClick={() => processStep('yes')}>Continue</button>
                    </div>
                </ModalBody>
            </Modal>
            <Modal className='approve-reject-modal' centered isOpen={rejectionModalVisibility} toggle={() => {
                setFeedbackText('')
                showRejectionModal(false)
            }}>
                <ModalBody className='p-0'>
                    <div className='approve-reject-modal-content'>
                        <div className="feedback">
                            <h4>Thanks for reviewing {influencer?.name?.split(' ')?.[0] ?? 'influencer'}'s idea!</h4>
                            <span>Every great idea needs a little fine-tuning. Your feedback will guide {influencer?.name?.split(' ')?.[0] ?? 'influencer'} to deliver their best work!</span>
                            <textarea placeholder='Share your thoughts, suggestions, or what’s missing...' value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} className='feedback-textarea mt-2' rows={5} />
                        </div>
                        <button className="theme-btn theme-btn-primary w-100" disabled={!feedbackText || submitting} onClick={() => processStep('no')}>Feedback & Decline</button>

                    </div>
                </ModalBody>
            </Modal>
        </>

    )
}

export default CurrentStep